import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';

import { IntegratedReviewsService } from '../../services/integrated-reviews.service';
import { TokenStorageService } from '../../core/services/token-storage.service';
import {
  IIntegratedReviewsInfo,
  IIntegratedReviewsLocation,
  IIntegratedReviewsMessages,
} from '../../models/integrated-reviews.model';
import { singleEmailValidator } from '../../shared/validators/single-email-validator';
import { phoneValidator } from '../../shared/validators/phone-validator';
import { BookmarkBannerComponent } from '../../shared/bookmark-banner.component';

type Tab = 'email' | 'text';

@Component({
  selector: 'app-integrated-reviews',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, NgxMaskDirective, BookmarkBannerComponent],
  providers: [provideNgxMask()],
  templateUrl: './integrated-reviews.component.html',
  styleUrl: './integrated-reviews.component.scss',
})
export class IntegratedReviewsComponent implements OnInit {
  private service = inject(IntegratedReviewsService);
  private tokenStorage = inject(TokenStorageService);
  private router = inject(Router);
  private fb = inject(FormBuilder);

  info: IIntegratedReviewsInfo | null = null;
  isLoading = true;
  loadError = '';

  activeTab: Tab = 'email';

  emailSubmitting = false;
  emailError = '';
  emailSuccess = '';

  textSubmitting = false;
  textError = '';
  textSuccess = '';

  emailForm: FormGroup = this.fb.group({
    recipientEmail: [null, [Validators.required, singleEmailValidator]],
  });

  textForm: FormGroup = this.fb.group({
    recipientPhone: [null, [Validators.required, phoneValidator]],
  });

  selectedLocationId = this.fb.control<number | null>(null);

  get hasMultipleLocations(): boolean {
    return (this.info?.locations.length ?? 0) > 1;
  }

  get hasAnyGoogleReviewLink(): boolean {
    return this.info?.locations.some(location => !!location.googleReviewLink) ?? false;
  }

  get selectedLocation(): IIntegratedReviewsLocation | null {
    return (
      this.info?.locations.find(location => location.id === this.selectedLocationId.value) ?? null
    );
  }

  get needsLocationChoice(): boolean {
    return this.hasMultipleLocations && !this.selectedLocation;
  }

  get emailSubject(): string {
    return this.info?.emailSubject ?? '';
  }

  get emailHtmlPreview(): string {
    return this.messages?.emailHtml ?? '';
  }

  get smsPreview(): string {
    return this.messages?.smsText ?? '';
  }

  get mailtoHref(): string {
    const body = this.messages?.emailPlainText;
    if (!body) {
      return '#';
    }
    return `mailto:?subject=${encodeURIComponent(this.emailSubject)}&body=${encodeURIComponent(body)}`;
  }

  ngOnInit(): void {
    this.service.getInfo().subscribe({
      next: data => {
        this.info = data;
        this.isLoading = false;
        if (data.locations.length === 1) {
          this.selectedLocationId.setValue(data.locations[0].id);
        }
      },
      error: err => {
        this.isLoading = false;
        if (err.status === 401) {
          this.tokenStorage.clearToken();
          this.router.navigate(['/login']);
        } else if (err.status === 403) {
          this.loadError = 'This feature is not enabled for your account.';
        } else {
          this.loadError = 'Unable to load page. Please try again later.';
        }
      },
    });
  }

  setTab(tab: Tab): void {
    this.activeTab = tab;
  }

  submitEmail(): void {
    this.emailError = '';
    this.emailSuccess = '';

    if (this.emailForm.invalid) {
      this.emailError = 'There are errors in this form.';
      return;
    }
    if (this.needsLocationChoice) {
      this.emailError = 'Please select the location you wish to send for.';
      return;
    }

    this.emailSubmitting = true;

    this.service
      .sendEmail({
        recipientEmail: this.emailForm.value.recipientEmail!,
        locationId: this.selectedLocationId.value,
      })
      .subscribe({
        next: () => {
          this.emailSubmitting = false;
          this.emailSuccess = 'Email sent successfully.';
          this.emailForm.reset();
        },
        error: err => {
          this.emailSubmitting = false;
          if (err.status === 401 || err.status === 403) {
            this.tokenStorage.clearToken();
            this.router.navigate(['/login']);
          } else if (err.status === 400) {
            this.emailError = 'Please enter a valid email address.';
          } else {
            this.emailError = 'There was an error sending the email. Please try again.';
          }
        },
      });
  }

  submitText(): void {
    this.textError = '';
    this.textSuccess = '';

    if (this.textForm.invalid) {
      this.textError = 'There are errors in this form.';
      return;
    }
    if (this.needsLocationChoice) {
      this.textError = 'Please select the location you wish to send for.';
      return;
    }

    this.textSubmitting = true;

    this.service
      .sendText({
        recipientPhone: this.textForm.value.recipientPhone!,
        locationId: this.selectedLocationId.value,
      })
      .subscribe({
        next: () => {
          this.textSubmitting = false;
          this.textSuccess = 'Text sent successfully.';
          this.textForm.reset();
        },
        error: err => {
          this.textSubmitting = false;
          if (err.status === 401 || err.status === 403) {
            this.tokenStorage.clearToken();
            this.router.navigate(['/login']);
          } else if (err.status === 400) {
            this.textError = 'Please enter a valid 10-digit phone number.';
          } else {
            this.textError = 'There was an error sending the text. Please try again.';
          }
        },
      });
  }

  private get messages(): IIntegratedReviewsMessages | null {
    if (this.needsLocationChoice) {
      return null;
    }
    return this.selectedLocation?.messages ?? this.info?.defaultMessages ?? null;
  }
}
